<template>
  <div class="return-rule-settings">
    <div class="box-title">
      <h4>
        <i class="fal fa-undo"></i>
        ຕັ້ງຄ່າ ການຕີກັບພັດສະດຸ
      </h4>
    </div>

    <p class="setting-hint">
      ຈຳນວນມື້ຢູ່ບ່ອນນີ້ ຄວບຄຸມທັງລະບົບພ້ອມກັນ — ປຸ່ມຕີກັບໃນແອັບ ແລະ ເວັບສາຂາ,
      ລາຍການທີ່ສະແດງໃນ tab ຕີກັບ, ຂໍ້ຄວາມ “ກະລຸນາຮັບເຄື່ອງພາຍໃນ N ວັນ” ທີ່ພິມໃສ່ບິນ
      ແລະ ວັນທີໃນຂໍ້ຄວາມແຈ້ງລູກຄ້າ (ຕິດຕາມພັດສະດຸ, Push, SMS, WhatsApp).
      ແກ້ບ່ອນດຽວ ທຸກບ່ອນຈະປ່ຽນຕາມ ບໍ່ຫຼົງກັນ.
    </p>

    <div v-if="loadError" class="notification is-warning setting-alert">
      <i class="fal fa-exclamation-triangle"></i>
      ບໍ່ສາມາດໂຫຼດຂໍ້ມູນການຕັ້ງຄ່າໄດ້ — ລະບົບຫຼັງບ້ານອາດຍັງບໍ່ຮອງຮັບ ຫຼື ເກີດຂໍ້ຜິດພາດ.
      <vs-button transparent class="setting-alert-action" @click="fetchData()">ລອງໃໝ່</vs-button>
    </div>

    <!--
      is_persisted=false ໝາຍຄວາມວ່າ ຕາຕະລາງ return_rule_settings ຍັງບໍ່ໄດ້ migrate.
      ຄ່າທີ່ເຫັນແມ່ນຄ່າມາດຕະຖານໃນ code (ຍັງໃຊ້ງານປົກກະຕິ) ແຕ່ບັນທຶກຍັງບໍ່ໄດ້.
    -->
    <div v-else-if="!isPersisted" class="notification is-warning setting-alert">
      <i class="fal fa-database"></i>
      ຕາຕະລາງການຕັ້ງຄ່າຍັງບໍ່ໄດ້ຕິດຕັ້ງ (migration ຍັງບໍ່ໄດ້ດຳເນີນ).
      ຄ່າລຸ່ມນີ້ແມ່ນ <strong>ຄ່າມາດຕະຖານໃນລະບົບ</strong> ເຊິ່ງໃຊ້ງານປົກກະຕິຢູ່ ແຕ່ຍັງບັນທຶກບໍ່ໄດ້.
    </div>

    <!--
      ບັນທຶກລົງຖານຂໍ້ມູນສຳເລັດແລ້ວ ແຕ່ລ້າງ cache ບໍ່ໄດ້ — ຄ່າໃໝ່ຈະຍັງບໍ່ທັນມີຜົນທຸກບ່ອນ
      ຈົນກວ່າ cache ຈະໝົດອາຍຸ. ໃຊ້ component ຮ່ວມກັບໜ້າສາຂາ ເພື່ອບໍ່ໃຫ້ຄຳເວົ້າຕ່າງກັນ.
    -->
    <CacheStaleNotice :visible="cacheStale">ບິນ ແລະ ຂໍ້ຄວາມແຈ້ງລູກຄ້າອາດຍັງໃຊ້ຄ່າມື້ເກົ່າ</CacheStaleNotice>

    <div class="setting-summary" v-if="clientPayload">
      <div class="setting-summary-item">
        <span class="setting-summary-label">ມື້ຕີກັບມາດຕະຖານ</span>
        <span class="setting-summary-value">{{ clientPayload.default_return_days }} ມື້</span>
      </div>
      <div class="setting-summary-item">
        <span class="setting-summary-label">ຢູ່ຕົ້ນທາງ ກ່ອນຕີກັບ ກວດສອບ</span>
        <span class="setting-summary-value">{{ clientPayload.origin_return_days }} ມື້</span>
      </div>
      <div class="setting-summary-item">
        <span class="setting-summary-label">ສຳເລັດບິນ Auto</span>
        <span class="setting-summary-value">{{ clientPayload.auto_sign_days }} ມື້</span>
      </div>
      <div class="setting-summary-item">
        <span class="setting-summary-label">Tab ທີ່ມີປຸ່ມດຳເນີນການ</span>
        <span class="setting-summary-value">{{ actionableTabsLabel }}</span>
      </div>
    </div>

    <div class="setting-group" v-for="row in rows" :key="row.group_key">
      <div class="setting-group-head">
        <h5>{{ row.label_la }}</h5>
        <span class="setting-group-key">{{ row.group_key }}</span>
      </div>

      <div class="columns is-multiline">
        <div class="column is-3">
          <label class="label">
            ມື້ທີ ຕີກັບ
            <span class="text-danger">{{ errorFor(row.group_key, 'return_days') }}</span>
          </label>
          <input
              class="input"
              type="number"
              :min="minDays"
              :max="maxDays"
              v-model.number="row.return_days"
          />
          <p class="setting-field-note">
            ຄ່າມາດຕະຖານ {{ row.defaults.return_days }} ມື້ ·
            ແຈ້ງລູກຄ້າມາຮັບກ່ອນວັນທີ +{{ noticeDays(row) }}
          </p>
        </div>

        <!--
          ບ່ອນສະແດງປຸ່ມດຳເນີນການ — ເລືອກ 7, 10 ຫຼື ທັງສອງ.
          ແຍກຕ່າງຫາກຈາກ “ມື້ທີ ຕີກັບ” ໂດຍເຈດຕະນາ: ຈຳນວນມື້ ຄື ກົດລະບຽບ,
          ສ່ວນ tab ຄື ບ່ອນທີ່ພະນັກງານເຮັດວຽກ — ສອງເລື່ອງນີ້ບໍ່ຈຳເປັນຕ້ອງຜູກກັນ.
        -->
        <div class="column is-6" v-if="row.supports_actionable_tabs">
          <label class="label">
            ສະແດງປຸ່ມດຳເນີນການຢູ່ tab
            <span class="text-danger">{{ errorFor(row.group_key, 'actionable_tabs') }}</span>
          </label>
          <div class="setting-tab-choices">
            <label
                v-for="tab in selectableTabs"
                :key="tab"
                class="setting-tab-choice"
                :class="{ 'is-checked': row.actionable_tabs.includes(tab) }"
            >
              <input
                  type="checkbox"
                  :value="tab"
                  :checked="row.actionable_tabs.includes(tab)"
                  @change="toggleTab(row, tab)"
              />
              <span>tab {{ tab }} ວັນ</span>
            </label>
          </div>
          <p class="setting-field-note">
            ເລືອກໄດ້ຫຼາຍກວ່າ 1 — ຖ້າເລືອກທັງສອງ ປຸ່ມຈະສະແດງທັງສອງ tab.
            ຕ້ອງເລືອກຢ່າງໜ້ອຍ 1 tab.
          </p>
        </div>

        <div class="column is-3" v-if="row.supports_origin_return_days">
          <label class="label">
            ຢູ່ຕົ້ນທາງ ກ່ອນຕີກັບ ກວດສອບ
            <span class="text-danger">{{ errorFor(row.group_key, 'origin_return_days') }}</span>
          </label>
          <input
              class="input"
              type="number"
              :min="minDays"
              :max="maxDays"
              v-model.number="row.origin_return_days"
          />
          <p class="setting-field-note">ຄ່າມາດຕະຖານ {{ row.defaults.origin_return_days }} ມື້</p>
        </div>

        <div class="column is-3" v-if="row.supports_auto_sign">
          <label class="label">
            ມື້ທີ ສຳເລັດບິນ Auto
            <span class="text-danger">{{ errorFor(row.group_key, 'auto_sign_days') }}</span>
          </label>
          <input
              class="input"
              type="number"
              :min="minDays"
              :max="maxDays"
              v-model.number="row.auto_sign_days"
          />
          <p class="setting-field-note">ຄ່າມາດຕະຖານ {{ row.defaults.auto_sign_days }} ມື້</p>
        </div>

        <!--
          ສາຂາຕ່າງປະເທດ ແລະ ສາຂາ Shopee ບໍ່ມີສຳເລັດບິນ Auto ແລະ ບໍ່ມີຂາທີ 2
          (ຕີກັບໄປ ກວດສອບ 371 / Shopee-lao 986 ໂດຍກົງ) — ຈຶ່ງບໍ່ມີຊ່ອງໃຫ້ຕັ້ງ,
          ບໍ່ແມ່ນເຊື່ອງໄວ້ ແຕ່ຍ້ອນວ່າບໍ່ມີບ່ອນໃດອ່ານຄ່ານັ້ນ.
        -->
        <div class="column is-6" v-if="!row.supports_auto_sign">
          <div class="setting-readonly-note">
            <i class="fal fa-info-circle"></i>
            ກຸ່ມນີ້ <strong>ບໍ່ມີສຳເລັດບິນ Auto</strong> ແລະ ຕີກັບໄປປາຍທາງສະເພາະຂອງມັນໂດຍກົງ
            ຈຶ່ງບໍ່ມີຄ່າ “ຢູ່ຕົ້ນທາງ” ໃຫ້ຕັ້ງ.
          </div>
        </div>
      </div>
    </div>

    <!--
      ວຽກອັດຕະໂນມັດ ຕອນກາງຄືນ — ແຕ່ກ່ອນ ການເປີດ/ປິດ ຕ້ອງໄປແກ້ code ໃນ Kernel.php
      ແລ້ວ deploy ໃໝ່ ຈຶ່ງຍ້າຍມາໄວ້ບ່ອນນີ້.
    -->
    <div class="setting-group setting-schedule-group" v-if="schedules.length">
      <div class="setting-group-head">
        <h5>ວຽກອັດຕະໂນມັດ ຕອນກາງຄືນ</h5>
      </div>

      <!--
        ແຕ່ກ່ອນຂໍ້ຄວາມນີ້ບອກວ່າ “ຮອບຕໍ່ໄປຈະເກັບກວາດໃຫ້ໝົດ” ເຊິ່ງຖືກສະເພາະ 2 ວຽກທຳອິດ.
        ວຽກ “ບວກມື້” ບໍ່ແມ່ນການເກັບກວາດ ແຕ່ເປັນການນັບ — ຂ້າມມື້ໃດ ມື້ນັ້ນຫາຍເລີຍ.
        ຈຶ່ງແຍກເວົ້າສອງແບບ ບໍ່ໃຫ້ຄົນອ່ານເຂົ້າໃຈຜິດວ່າປິດແລ້ວບໍ່ມີຫຍັງເສຍ.
      -->
      <p class="setting-field-note setting-schedule-hint">
        ວຽກ <strong>ເກັບກວາດ</strong> (2 ອັນທຳອິດ): ປິດແລ້ວຈະຂ້າມຮອບກາງຄືນນັ້ນ — ບິນຈະບໍ່ຫາຍໄປ
        ແລະ ຈະຖືກເກັບກວາດໃນຮອບຕໍ່ໄປຫຼັງເປີດຄືນ.<br/>
        ວຽກ <strong>ນັບມື້</strong>: ປິດມື້ໃດ ມື້ນັ້ນຈະບໍ່ຖືກນັບ ແລະ <strong>ບໍ່ໄດ້ນັບຍ້ອນຫຼັງ</strong>.<br/>
        ທັງໝົດນີ້ ການສັ່ງດ້ວຍມື (artisan) ຍັງເຮັດໄດ້ຢູ່ປົກກະຕິ.
      </p>

      <!--
        ອ່ານຄ່າບໍ່ໄດ້ ≠ ຍັງບໍ່ໄດ້ migrate. ຖ້າອ່ານບໍ່ໄດ້ ຝ່າຍ scheduler ຈະ “ບໍ່ແລ່ນ” ທຸກວຽກ
        (ປອດໄພໄວ້ກ່ອນ) ເຖິງແມ່ນສະວິດລຸ່ມນີ້ຈະສະແດງເປັນເປີດກໍຕາມ.
      -->
      <div v-if="isSchedulePersisted && !isScheduleReadable" class="notification is-danger setting-alert">
        <i class="fal fa-exclamation-triangle"></i>
        ອ່ານຄ່າການຕັ້ງຄ່າວຽກອັດຕະໂນມັດ<strong>ບໍ່ໄດ້</strong> —
        ເພື່ອຄວາມປອດໄພ ລະບົບຈະ<strong>ບໍ່ແລ່ນ</strong>ວຽກໃດໆລຸ່ມນີ້ຈົນກວ່າຈະອ່ານໄດ້ຄືນ.
        ກະລຸນາແຈ້ງທີມ IT.
      </div>

      <div v-else-if="!isSchedulePersisted" class="notification is-warning setting-alert">
        <i class="fal fa-database"></i>
        ຕາຕະລາງ <strong>return_schedule_settings</strong> ຍັງບໍ່ໄດ້ migrate —
        ສະຖານະລຸ່ມນີ້ແມ່ນຄ່າມາດຕະຖານ (ເປີດທັງໝົດ) ແລະ ຍັງບັນທຶກບໍ່ໄດ້.
      </div>

      <!--
        ອີງໃສ່ຄ່າທີ່ບັນທຶກແລ້ວ (baseline) ບໍ່ແມ່ນຄ່າທີ່ກຳລັງແກ້ຢູ່ໜ້າຈໍ —
        ຖ້າອີງໃສ່ຄ່າໜ້າຈໍ ພໍກົດສະວິດປຸບ ມັນຈະບອກວ່າ “ປິດແລ້ວ” ທັງທີ່ຫຼັງບ້ານຍັງເປີດຢູ່.
      -->
      <div v-else-if="disabledScheduleLabels.length" class="notification is-danger setting-alert">
        <i class="fal fa-pause-circle"></i>
        ຕອນນີ້ <strong>{{ disabledScheduleLabels.join(', ') }}</strong> ຖືກປິດຢູ່ —
        ຮອບກາງຄືນຂອງວຽກນັ້ນຈະບໍ່ເຮັດວຽກຈົນກວ່າຈະເປີດຄືນ.
      </div>

      <div v-if="changedSchedules.length" class="notification is-warning setting-alert">
        <i class="fal fa-pencil"></i>
        ມີການປ່ຽນສະຖານະທີ່<strong>ຍັງບໍ່ໄດ້ບັນທຶກ</strong> — ກົດ “ບັນທຶກການຕັ້ງຄ່າ” ຈຶ່ງຈະມີຜົນ.
      </div>

      <div class="setting-schedule-row" v-for="schedule in schedules" :key="schedule.schedule_key">
        <div class="setting-schedule-info">
          <span class="setting-schedule-label">{{ schedule.label_la }}</span>
          <span class="setting-schedule-meta">
            {{ schedule.run_at_la }} · <code>{{ schedule.command_signature }}</code>
          </span>
          <!--
            ຄຳເຕືອນສະເພາະວຽກ — ມາຈາກ backend ບໍ່ແມ່ນຂຽນຄ້າງໄວ້ບ່ອນນີ້ ເພາະວ່າ
            “ວຽກນີ້ເຮັດຫຍັງກັບລະບົບ” ແມ່ນເລື່ອງຂອງ code ຝ່າຍນັ້ນ. ວຽກໃດບໍ່ມີ ກໍບໍ່ສະແດງ.
          -->
          <span v-if="schedule.note_la" class="setting-schedule-note">
            <i class="fal fa-exclamation-circle"></i>
            {{ schedule.note_la }}
          </span>
          <span class="text-danger">{{ scheduleErrors[schedule.schedule_key] || '' }}</span>
        </div>
        <div class="setting-schedule-switch">
          <vs-switch v-model="schedule.is_enabled" :disabled="!canEditSchedules">
            <template #off>ປິດ</template>
            <template #on>ເປີດ</template>
          </vs-switch>
        </div>
      </div>
    </div>

    <div class="setting-actions">
      <vs-button :loading="saving" :disabled="loadError || !hasChanges" @click="save()">
        <i class="fal fa-check-circle"></i>
        ບັນທຶກການຕັ້ງຄ່າ
      </vs-button>
      <vs-button dark transparent :disabled="saving || !hasChanges" @click="fetchData()">
        ຍົກເລີກການແກ້ໄຂ
      </vs-button>
    </div>

    <p class="setting-footnote">
      ໝາຍເຫດ: ແອັບພະນັກງານ ແລະ ເວັບສາຂາ ໂຫຼດຄ່ານີ້ຕອນເປີດໂປຣແກຣມ ແລ້ວເກັບໄວ້ໃຊ້
      ເພື່ອບໍ່ໃຫ້ການພິມບິນຊ້າ — ຫຼັງແກ້ຄ່າແລ້ວ ເຄື່ອງທີ່ເປີດຄ້າງໄວ້ຈະຮັບຄ່າໃໝ່ຕອນເປີດຄືນ.
    </p>

    <Loading v-if="isLoading"/>
  </div>
</template>

<script>
import Loading from '@/views/Loading';
import CacheStaleNotice from '@/components/CacheStaleNotice';

/**
 * ຕັ້ງຄ່າ ການຕີກັບພັດສະດຸ — GET/PUT api/admin/return-rule-settings.
 *
 * Every day count the returning rules use lives behind this one screen, so the fields here are
 * the same numbers the API gate, the returning list, the printed bill and the customer arrival
 * messages all read. The backend keeps them in step; this page only has to send them.
 */
export default {
  name: 'ReturnRuleSettings',

  components: { Loading, CacheStaleNotice },

  data: () => ({
    isLoading: false,
    saving: false,
    loadError: false,
    isPersisted: false,
    rows: [],
    /// The nightly ON/OFF switches. Tracked separately from `isPersisted` because the two
    /// tables come from different migrations — one can be live while the other is not.
    schedules: [],
    isSchedulePersisted: false,
    /// Distinct from persisted: false means the switches could not be READ, so the backend is
    /// refusing to run either sweep even though the rows below render as enabled defaults.
    isScheduleReadable: true,
    /// Set when a save committed but the shared cache could not be invalidated: the numbers are
    /// stored, yet readers may keep the previous ones until the cache expires.
    cacheStale: false,
    scheduleErrors: {},
    /// What the server held when the page last loaded. Every save diffs against this, so a tab
    /// left open cannot resubmit — and silently revert — something another admin changed since.
    baseline: { groups: {}, schedules: {} },
    /// What the last save actually sent. Laravel indexes its validation errors by position in
    /// the submitted arrays, and those are now the CHANGED subset, not `rows` / `schedules`.
    lastSent: { groups: [], schedules: [] },
    clientPayload: null,
    /// Mirrors ReturnRuleSettingsService::MIN_DAYS / MAX_DAYS. The input bounds are a typo
    /// guard only — the server validates independently and is the authority.
    minDays: 1,
    maxDays: 365,
    /// The tabs an operator may put the buttons on. The backend taxonomy also has a 3-day tab,
    /// but that one is an aging bucket staff do not action from, so it is not offered here.
    selectableTabs: [7, 10],
    serverErrors: {},
  }),

  created() {
    this.fetchData();
  },

  computed: {
    actionableTabsLabel() {
      const tabs = (this.clientPayload && this.clientPayload.actionable_due_days_list) || [];
      if (!tabs.length) {
        return '-';
      }
      return tabs.map(tab => `${tab} ວັນ`).join(' + ');
    },

    /**
     * Switches may only be touched once they are actually stored — otherwise the operator would
     * be flipping something the save cannot carry.
     */
    canEditSchedules() {
      return this.isSchedulePersisted;
    },

    /**
     * What is disabled ON THE SERVER, from the baseline rather than from the switches on screen.
     * Reading the live switches would make the banner announce a stopped sweep the instant
     * someone flips one, before anything has been saved.
     */
    disabledScheduleLabels() {
      return this.schedules
        .filter(schedule => this.baseline.schedules[schedule.schedule_key] === false)
        .map(schedule => schedule.label_la);
    },

    /**
     * Only the rows the operator actually changed. Sending the untouched ones would let a stale
     * tab overwrite another admin's edit with the values it loaded minutes ago — including
     * re-enabling a schedule someone else just stopped.
     */
    changedGroups() {
      if (!this.isPersisted) {
        return [];
      }

      return this.rows
        .map(row => this.payloadFor(row))
        .filter(payload => JSON.stringify(payload) !== this.baseline.groups[payload.group_key]);
    },

    changedSchedules() {
      if (!this.isSchedulePersisted) {
        return [];
      }

      return this.schedules
        .filter(schedule => (schedule.is_enabled === true) !== this.baseline.schedules[schedule.schedule_key])
        .map(schedule => ({
          schedule_key: schedule.schedule_key,
          is_enabled: schedule.is_enabled === true,
        }));
    },

    hasChanges() {
      return this.changedGroups.length > 0 || this.changedSchedules.length > 0;
    },
  },

  methods: {
    /**
     * Toggle a tab, refusing to clear the last one: with no actionable tab the returning list
     * still shows the bills but offers no way to act on them, which reads as a broken screen.
     * The server refuses an empty selection too; this just avoids sending one.
     */
    toggleTab(row, tab) {
      const tabs = Array.isArray(row.actionable_tabs) ? [...row.actionable_tabs] : [];
      const index = tabs.indexOf(tab);

      if (index === -1) {
        tabs.push(tab);
      } else if (tabs.length > 1) {
        tabs.splice(index, 1);
      } else {
        this.$notification.openNotification(
          { title: 'ຕັ້ງຄ່າ ການຕີກັບ', text: 'ຕ້ອງເລືອກຢ່າງໜ້ອຍ 1 tab' },
          'top-right', 'warn', 4000
        );
        return;
      }

      tabs.sort((a, b) => a - b);
      row.actionable_tabs = tabs;
    },

    noticeDays(row) {
      const days = Number(row.return_days);
      return Number.isFinite(days) ? days + 1 : '-';
    },

    errorFor(groupKey, field) {
      const groupErrors = this.serverErrors[groupKey];
      return (groupErrors && groupErrors[field]) || '';
    },

    /**
     * index() and a successful update() return the same payload, so both land here — the page
     * can never end up describing the settings differently depending on how it got them.
     */
    applyResponse(data) {
      // Cloned so editing an input never mutates what we would fall back to on cancel.
      // Falling back to what is already on screen, not to [], so an unexpected response shape
      // cannot blank the form the operator is looking at.
      this.rows = JSON.parse(JSON.stringify(data.rows || this.rows));
      this.schedules = JSON.parse(JSON.stringify(data.schedules || this.schedules));
      this.clientPayload = data.client_payload || this.clientPayload;
      this.isPersisted = data.is_persisted === true;
      this.isSchedulePersisted = data.is_schedule_persisted === true;
      /// Absent on an older backend, which had no unreadable state — treat that as readable so
      /// the page does not raise an alarm about a field the server never sent.
      this.isScheduleReadable = data.is_schedule_readable !== false;

      this.captureBaseline();
    },

    /**
     * Snapshot what the server currently holds, in exactly the shape a save would send, so the
     * diff is a string comparison rather than a field-by-field one that drifts as fields are
     * added.
     */
    captureBaseline() {
      const groups = {};
      this.rows.forEach(row => {
        groups[row.group_key] = JSON.stringify(this.payloadFor(row));
      });

      const schedules = {};
      this.schedules.forEach(schedule => {
        schedules[schedule.schedule_key] = schedule.is_enabled === true;
      });

      this.baseline = { groups, schedules };
    },

    fetchData() {
      this.isLoading = true;
      this.serverErrors = {};
      this.scheduleErrors = {};
      /// A fresh load of the current server state is where a warning about a PAST save stops
      /// being useful — by then the cache has usually expired.
      this.cacheStale = false;
      this.$axios.get('return-rule-settings').then(res => {
        this.applyResponse((res.data && res.data.data) || {});
        this.loadError = false;
        this.isLoading = false;
      }).catch(() => {
        // Backend not deployed yet / outage: say so rather than rendering empty
        // inputs that look like real settings.
        this.isLoading = false;
        this.loadError = true;
        this.rows = [];
        this.schedules = [];
        this.clientPayload = null;
        this.$notification.OpenNotification_Error_Server('top-right', 'danger', 5000);
      });
    },

    save() {
      this.saving = true;
      this.serverErrors = {};
      this.scheduleErrors = {};

      /// Changed rows only, and each half only once its own table exists. The request is one
      /// transaction, so including switches the server would refuse with TABLE_MISSING would
      /// roll back the day counts too — an unmigrated switch table must not block editing a
      /// threshold, and vice versa.
      const payload = {};
      if (this.changedGroups.length) {
        payload.groups = this.changedGroups;
      }
      if (this.changedSchedules.length) {
        payload.schedules = this.changedSchedules;
      }

      this.lastSent = { groups: payload.groups || [], schedules: payload.schedules || [] };

      this.$axios.put('return-rule-settings', payload).then(res => {
        this.saving = false;
        this.applyResponse((res.data && res.data.data) || {});

        /// The rows are saved, but the shared cache could not be invalidated — so the printed
        /// bill, the return gate and the arrival messages may keep using the PREVIOUS numbers
        /// for a few minutes. Saying "ບັນທຶກສຳເລັດ" alone would have the operator believe the
        /// change is already live everywhere.
        if (res.data && res.data.cache_stale) {
          this.cacheStale = true;
          this.$notification.openNotification(
            {
              title: 'ບັນທຶກແລ້ວ ແຕ່ຍັງບໍ່ທັນມີຜົນທຸກບ່ອນ',
              text: 'ບາງເຄື່ອງອາດຍັງໃຊ້ຄ່າເກົ່າ ປະມານ 5 ນາທີ',
            },
            'top-right', 'warn', 7000
          );
          return;
        }

        this.cacheStale = false;
        this.$notification.OpenNotification_EditItem_OnSuccess('top-right', 'dark', 3000);
      }).catch(error => {
        this.saving = false;
        const status = error.response && error.response.status;

        if (status === 422) {
          this.applyValidationErrors(error.response.data);
          return;
        }
        this.$notification.OpenNotification_Error_Server('top-right', 'danger', 5000);
      });
    },

    /**
     * Only the ສາຂາທົ່ວໄປ row carries hop 2 and auto-sign; sending them for the other groups
     * would ask the server to store numbers nothing reads.
     */
    payloadFor(row) {
      const payload = {
        group_key: row.group_key,
        return_days: row.return_days,
      };

      if (row.supports_actionable_tabs) {
        payload.actionable_tabs = row.actionable_tabs;
      }
      if (row.supports_origin_return_days) {
        payload.origin_return_days = row.origin_return_days;
      }
      // `is_auto_sign_enabled` is intentionally not sent: whether a group is auto-signed at
      // all is a branch-id rule the settings table does not override, so it is read-only.
      if (row.supports_auto_sign) {
        payload.auto_sign_days = row.auto_sign_days;
      }

      return payload;
    },

    /**
     * A 422 arrives either as Laravel field errors keyed `groups.0.return_days` (which index a
     * position in the array we just sent) or as our own per-group `failures` list. Both are
     * mapped back onto the group so the message lands on the field that caused it.
     */
    applyValidationErrors(body) {
      const errors = {};
      const scheduleErrors = {};

      const fieldErrors = (body && body.errors) || {};
      Object.keys(fieldErrors).forEach(key => {
        /// The indices are positions in the arrays we SENT — the changed subset — so they are
        /// resolved through `lastSent`. Indexing `rows` / `schedules` here would put the message
        /// on whichever row happens to sit at that position on screen.
        const scheduleMatch = key.match(/^schedules\.(\d+)\.\w+$/);
        if (scheduleMatch) {
          const sent = this.lastSent.schedules[Number(scheduleMatch[1])];
          if (sent) {
            scheduleErrors[sent.schedule_key] = [].concat(fieldErrors[key])[0];
          }
          return;
        }

        const match = key.match(/^groups\.(\d+)\.(\w+)$/);
        if (!match) {
          return;
        }
        const sentGroup = this.lastSent.groups[Number(match[1])];
        if (!sentGroup) {
          return;
        }
        errors[sentGroup.group_key] = errors[sentGroup.group_key] || {};
        errors[sentGroup.group_key][match[2]] = [].concat(fieldErrors[key])[0];
      });

      ((body && body.failures) || []).forEach(failure => {
        errors[failure.group_key] = errors[failure.group_key] || {};
        errors[failure.group_key].return_days = this.failureMessage(failure.error);
      });

      ((body && body.schedule_failures) || []).forEach(failure => {
        scheduleErrors[failure.schedule_key] = this.failureMessage(failure.error);
      });

      this.serverErrors = errors;
      this.scheduleErrors = scheduleErrors;
      this.$notification.openNotification(
        { title: 'ຕັ້ງຄ່າ ການຕີກັບ', text: 'ບັນທຶກບໍ່ສຳເລັດ — ກະລຸນາກວດຄ່າທີ່ໃສ່ຄືນ' },
        'top-right', 'danger', 5000
      );
    },

    failureMessage(errorCode) {
      if (errorCode === 'TABLE_MISSING') {
        return 'ຖານຂໍ້ມູນຝັ່ງ backend ຍັງບໍ່ພ້ອມ (migration ຍັງບໍ່ໄດ້ດຳເນີນ)';
      }
      if (errorCode === 'UNKNOWN_GROUP') {
        return 'ປະເພດສາຂາບໍ່ຖືກຕ້ອງ';
      }
      if (errorCode === 'UNKNOWN_SCHEDULE') {
        return 'ວຽກອັດຕະໂນມັດບໍ່ຖືກຕ້ອງ';
      }
      /// ຕາຕະລາງມີແລ້ວ ແຕ່ຂຽນແຖວນີ້ບໍ່ໄດ້ — ສ່ວນຫຼາຍແມ່ນ migration ໃໝ່ຍັງບໍ່ໄດ້ດຳເນີນ
      /// ຈຶ່ງຍັງບໍ່ຮູ້ຈັກວຽກອັນນີ້. ບອກໃຫ້ຊັດ ດີກວ່າປ່ອຍໃຫ້ຕົກໄປໃສ່ຂໍ້ຄວາມ “ຈຳນວນມື້”.
      if (errorCode === 'WRITE_FAILED') {
        return 'ບັນທຶກວຽກນີ້ບໍ່ໄດ້ — ຖານຂໍ້ມູນຍັງບໍ່ຮູ້ຈັກວຽກອັນນີ້ (migration ໃໝ່ຍັງບໍ່ໄດ້ດຳເນີນ)';
      }
      return 'ຈຳນວນມື້ບໍ່ຢູ່ໃນຂອບເຂດທີ່ອະນຸຍາດ';
    },
  },
};
</script>

<style lang="scss" scoped>
.return-rule-settings {
  padding: 4px 2px 24px;
}

.setting-hint {
  color: #6b7280;
  line-height: 1.7;
  margin: 8px 0 4px;
}

.setting-alert {
  margin-top: 12px;

  i {
    margin-right: 6px;
  }
}

.setting-alert-action {
  display: inline-block;
  margin-left: 8px;
}

.setting-summary {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin: 16px 0 6px;
}

.setting-summary-item {
  flex: 1 1 180px;
  background: #f5f7fb;
  border-radius: 8px;
  padding: 10px 14px;
}

.setting-summary-label {
  display: block;
  font-size: 13px;
  color: #6b7280;
}

.setting-summary-value {
  display: block;
  font-size: 18px;
  font-weight: 600;
  color: #195BFF;
}

.setting-group {
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  padding: 14px 16px 4px;
  margin-top: 16px;
}

.setting-group-head {
  display: flex;
  align-items: baseline;
  gap: 10px;
  margin-bottom: 6px;

  h5 {
    font-size: 17px;
    font-weight: 600;
    margin: 0;
  }
}

.setting-group-key {
  font-size: 12px;
  color: #9ca3af;
}

.setting-field-note {
  font-size: 12px;
  color: #9ca3af;
  margin-top: 4px;
}

.setting-tab-choices {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.setting-tab-choice {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 8px 14px;
  cursor: pointer;
  user-select: none;
  background: #ffffff;

  &:hover {
    border-color: #c7d2fe;
  }

  &.is-checked {
    border-color: #195BFF;
    background: rgba(25, 91, 255, 0.08);
    color: #195BFF;
    font-weight: 600;
  }

  input {
    cursor: pointer;
  }
}

.setting-schedule-group {
  padding-bottom: 16px;
}

.setting-schedule-hint {
  margin: 0 0 10px;
  line-height: 1.7;
}

.setting-schedule-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  border-top: 1px solid #f3f4f6;
  padding: 12px 2px;

  &:first-of-type {
    border-top: 0;
  }
}

.setting-schedule-label {
  display: block;
  font-weight: 600;
}

.setting-schedule-meta {
  display: block;
  font-size: 12px;
  color: #9ca3af;
  margin-top: 2px;

  code {
    background: #f3f4f6;
    border-radius: 4px;
    padding: 1px 5px;
    color: #6b7280;
  }
}

// ຄຳເຕືອນສະເພາະວຽກ — ເຂັ້ມກວ່າ meta ເພື່ອບໍ່ໃຫ້ອ່ານຜ່ານ ແຕ່ບໍ່ແມ່ນສີແດງ
// ເພາະວ່າມັນເປັນຄຳອະທິບາຍ ບໍ່ແມ່ນຂໍ້ຜິດພາດ.
.setting-schedule-note {
  display: block;
  font-size: 12px;
  color: #b45309;
  background: #fffbeb;
  border-left: 3px solid #f59e0b;
  border-radius: 0 6px 6px 0;
  padding: 6px 10px;
  margin-top: 6px;
  line-height: 1.65;
  max-width: 640px;

  i {
    margin-right: 5px;
  }
}

.setting-schedule-switch {
  flex: 0 0 auto;

  // vs-switch stretches to fill its parent unless it is pinned, same fix the sorting-mode
  // and route-system screens carry.
  ::v-deep .vs-switch {
    width: auto !important;
    max-width: fit-content;
  }
}

.setting-readonly-note {
  background: #f9fafb;
  border-radius: 8px;
  padding: 10px 12px;
  color: #6b7280;
  margin-top: 26px;

  i {
    margin-right: 6px;
  }
}

.setting-actions {
  display: flex;
  align-items: center;
  margin-top: 20px;

  i {
    margin-right: 6px;
  }
}

.setting-footnote {
  font-size: 13px;
  color: #9ca3af;
  margin-top: 14px;
  line-height: 1.7;
}

.text-danger {
  color: crimson;
  font-size: 13px;
  margin-left: 6px;
}
</style>
